import {jwtDecode} from 'jwt-decode';
import Cookies from 'js-cookie';

import {useAuth, clearUserSession} from '@/composables/useAuth';
import {SessionFound, User} from '@/models/auth';
import {STORAGE_KEYS} from '@/utils/constants';
import {isNative} from "@/utils/platform.ts";
import {apiFetch} from '@/services/ApiClient.ts';
import {StorageKey, StorageService} from "@/services/storageService.ts";
import {Result} from "@/utils/types.ts";
import { closeSessionModal } from "@/utils/notifications.ts";
import {SessionHub} from "@/utils/sessionHub.ts";

interface LoginResponse {
    authUrl: string;
    token: string;
    tokenSource: "cookie" | "storage" | "url";
    status: "success" | "failedUpdateChatSettings" | null;
}

interface ErrorResponse {
    error: string;
}

export class AuthService {
    static async findSession(): Promise<SessionFound | null> {
        let tokenValue: string | null = null;
        let source: string = 'cookie';

        const isValidToken = (token: string | null | undefined): token is string => {
            if (!token) return false;
            const jwtRegex = /^[A-Za-z0-9-_=]+\.[A-Za-z0-9-_=]+\.[A-Za-z0-9-_=]+$/;
            return jwtRegex.test(token.trim());
        };

        if (isNative) {
            const accessToken = await StorageService.Get<string>("ACCESS_TOKEN");
            if (isValidToken(accessToken)) {
                tokenValue = accessToken.trim();
                source = 'storage';
            }
        }

        if (!tokenValue) {
            const cookieToken = this.getCookie('AccessToken');
            if (isValidToken(cookieToken)) {
                tokenValue = cookieToken.trim();
                source = 'cookie';
            }
        }

        if (!tokenValue) {
            const urlParams = new URLSearchParams(window.location.search);
            const urlToken = urlParams.get('token');
            if (isValidToken(urlToken)) {
                tokenValue = urlToken.trim();
                source = 'url';
                sessionStorage.setItem('iframe_active_token', tokenValue);
            }
        }

        if (!tokenValue) {
            const fallbackSessionToken = sessionStorage.getItem('iframe_active_token');
            if (isValidToken(fallbackSessionToken)) {
                tokenValue = fallbackSessionToken.trim();
                source = 'url';
            }
        }

        if (tokenValue) {
            const userResult = await this.processToken(tokenValue);
            if (!userResult) {
                const { logout } = useAuth();

                await logout();
                return null;
            }

            return {
                token: tokenValue,
                source: source,
                user: userResult
            };
        }
        return null;
    }

    private static async processToken(token: string): Promise<User | null> {
        try {
            const decoded: any = jwtDecode(token);

            const currentTime = Math.floor(Date.now() / 1000);
            if (decoded.exp && decoded.exp < currentTime) {
                console.error("Token has expired.");
                return null;
            }

            const plainData = JSON.parse(decoded.plainData);

            return {
                Id: plainData.Id,
                Email: plainData.Em || '',
                FirstName: plainData.Fn,
                LastName: plainData.Ln || '',
                Services: plainData.Srv || [],
                RecentServices: plainData.Rvs || [],
                Roles: plainData.Rl,
                TokenExp: decoded.exp
            };
        } catch (e) {
            console.error("Invalid token found:", e);
            return null;
        }
    }

    static async loginRequest(payload: any): Promise<Result<LoginResponse>> {
        const response = await apiFetch('/loginSso', {
            method: 'POST',
            body: JSON.stringify(payload),
        });

        const resp = await response.json()

        if (resp.status !== 'success') {
            return {
                success: false,
                error: { msg: resp.error?.msg }
            };
        }

        resp.data["preferences"] = {
            language: 'enUS',
        }

        return {
            success: true,
            data: resp.data
        };
    }

    static async logout(): Promise<void> {
        await this.clearSessionState();
        await closeSessionModal();
        const keysToRemove: Array<StorageKey> = ["IS_LOGGED_IN", "ACCESS_TOKEN"];
        await Promise.all(
            keysToRemove.map(key => StorageService.Remove(key))
        );
        Cookies.remove(STORAGE_KEYS.ACCESS_TOKEN, { path: '/' });
        sessionStorage.removeItem('iframe_active_token');
        sessionStorage.clear();
    }

    static async clearSessionState() {
        clearUserSession();
    }

    static async isClockedInRequest() {
        const response = await apiFetch('/attendanceStatus', {
            method: 'POST',
            body: JSON.stringify({}),
        });

        const data = await response.json();
        if (!response.ok) throw data;
        return data;
    }

    private static getCookie(name: string): string | undefined {
        return Cookies.get(name);
    }
}